import { useState, useRef, useEffect, useCallback } from 'react';
import { APP, getFileBadgeColor } from './tokens';
import type { Folder } from '../../types';

// ─── Types ──────────────────────────────────────

export interface RecentItem {
  id: string;
  title: string;
  lastMessageAt: Date;
  /** File type for the badge (e.g. "pdf", "docx", "png"). Falls back to title extension. */
  fileType?: string;
  /** Short operation label shown below the title (e.g. "Signed", "Converted"). */
  operation?: string;
  pinned?: boolean;
  folderId?: string;
  /** Risk nudge level from auto/manual clause review */
  riskNudge?: 'attention' | 'reconsider' | null;
  /** Count of high-risk flags (critical + high severity) */
  highRiskCount?: number;
  /** Count of medium-risk flags */
  mediumRiskCount?: number;
}

interface RecentPanelProps {
  items: RecentItem[];
  folders: Folder[];
  /** Header title shown at the top of the panel. Defaults to "Recent". */
  title?: string;
  onOpen: (id: string) => void;
  onRename: (id: string) => void;
  onStar: (id: string, pinned: boolean) => void;
  onAddToFolder: (id: string, folderId: string) => void;
  onDelete: (id: string) => void;
}

// ─── Helpers ────────────────────────────────────

function timeAgo(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isYesterday(date: Date): boolean {
  const y = new Date();
  y.setDate(y.getDate() - 1);
  return isSameDay(date, y);
}

interface DateGroup {
  label: string;
  items: RecentItem[];
}

function groupByDate(items: RecentItem[]): DateGroup[] {
  const now = new Date();
  const todayItems: RecentItem[] = [];
  const yesterdayItems: RecentItem[] = [];
  const last7Items: RecentItem[] = [];
  const olderItems: RecentItem[] = [];

  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  for (const item of items) {
    if (isSameDay(item.lastMessageAt, now)) {
      todayItems.push(item);
    } else if (isYesterday(item.lastMessageAt)) {
      yesterdayItems.push(item);
    } else if (item.lastMessageAt >= sevenDaysAgo) {
      last7Items.push(item);
    } else {
      olderItems.push(item);
    }
  }

  const groups: DateGroup[] = [];
  if (todayItems.length) groups.push({ label: 'TODAY', items: todayItems });
  if (yesterdayItems.length) groups.push({ label: 'YESTERDAY', items: yesterdayItems });
  if (last7Items.length) groups.push({ label: 'LAST 7 DAYS', items: last7Items });
  if (olderItems.length) groups.push({ label: 'OLDER', items: olderItems });
  return groups;
}

/** Derive file type from title extension. */
function getFileTypeFromTitle(title: string): string {
  const ext = title.split('.').pop()?.toLowerCase() ?? '';
  if (['pdf'].includes(ext)) return 'pdf';
  if (['doc', 'docx'].includes(ext)) return 'doc';
  if (['png', 'jpg', 'jpeg', 'webp', 'avif', 'gif', 'heic', 'bmp', 'tiff', 'svg'].includes(ext)) return 'img';
  if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'].includes(ext)) return 'mp3';
  if (['xlsx', 'xls', 'csv'].includes(ext)) return 'xls';
  if (['pptx', 'ppt'].includes(ext)) return 'ppt';
  return 'pdf'; // fallback
}

// ─── Icons ──────────────────────────────────────

function DotsIcon() {
  return (
    <svg width="14" height="24" viewBox="0 0 14 24" fill="none">
      <circle cx="7" cy="5" r="1.5" fill={APP.iconDots} />
      <circle cx="7" cy="12" r="1.5" fill={APP.iconDots} />
      <circle cx="7" cy="19" r="1.5" fill={APP.iconDots} />
    </svg>
  );
}

function RenameIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M9.5 2L12 4.5L5 11.5H2.5V9L9.5 2Z" stroke={APP.iconDefault} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 1.5L8.5 5H12L9 7.5L10 11L7 9L4 11L5 7.5L2 5H5.5L7 1.5Z" stroke={APP.iconDefault} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FolderMenuIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M1 4C1 3.4 1.4 3 2 3H5.5L6.5 4.5H12C12.6 4.5 13 4.9 13 5.5V11C13 11.6 12.6 12 12 12H2C1.4 12 1 11.6 1 11V4Z" stroke={APP.iconDefault} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FolderChevron() {
  return (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
      <path d="M2 3L4 5L6 3" stroke={APP.textMuted} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrashMenuIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2 4H12M5 4V2.5H9V4M5.5 6.5V10.5M8.5 6.5V10.5M3 4L3.5 12H10.5L11 4H3Z" stroke="#C0392B" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── File type badge (tiny solid square) ────────

function FileTypeBadge({ type }: { type?: string | null }) {
  const t = (type || 'file').toUpperCase();
  const bg = getFileBadgeColor(type);
  return (
    <div
      style={{
        width: 14,
        height: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        borderRadius: 2,
        backgroundColor: bg,
      }}
    >
      <span style={{ color: '#FFFFFF', fontFamily: APP.fontFamily, fontSize: 6, fontWeight: 700, lineHeight: '8px' }}>
        {t.slice(0, 3)}
      </span>
    </div>
  );
}

// ─── Context menu ───────────────────────────────

interface ContextMenuProps {
  item: RecentItem;
  folders: Folder[];
  anchorRect: DOMRect;
  onRename: () => void;
  onStar: () => void;
  onAddToFolder: (folderId: string) => void;
  onDelete: () => void;
  onClose: () => void;
}

function ContextMenu({ item, folders, anchorRect, onRename, onStar, onAddToFolder, onDelete, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [folderOpen, setFolderOpen] = useState(false);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const menuTop = anchorRect.bottom + 4;
  const menuRight = window.innerWidth - anchorRect.right;

  return (
    <>
      {/* Invisible backdrop to catch outside clicks */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 999 }}
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      />
      <div
        ref={menuRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: menuTop,
          right: menuRight,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column' as const,
          width: 200,
          borderRadius: 9,
          padding: 5,
          gap: 1,
          backgroundColor: APP.bgContextMenu,
          border: `1px solid ${APP.borderContextMenu}`,
          boxShadow: APP.shadowContextMenu,
          fontFamily: APP.fontFamily,
        }}
    >
      {/* Rename */}
      <button
        type="button"
        onClick={() => { onRename(); onClose(); }}
        onMouseEnter={() => setActiveIdx(0)}
        onMouseLeave={() => setActiveIdx(null)}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '8px 10px',
          gap: 10,
          borderRadius: 6,
          backgroundColor: activeIdx === 0 ? APP.bgMenuHover : 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: APP.fontFamily,
          fontSize: 13,
          fontWeight: 400,
          color: APP.textPrimary,
          lineHeight: '16px',
          textAlign: 'left',
        }}
      >
        <RenameIcon />
        Rename
      </button>

      {/* Star */}
      <button
        type="button"
        onClick={() => { onStar(); onClose(); }}
        onMouseEnter={() => setActiveIdx(1)}
        onMouseLeave={() => setActiveIdx(null)}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '8px 10px',
          gap: 10,
          borderRadius: 6,
          backgroundColor: activeIdx === 1 ? APP.bgMenuHover : 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: APP.fontFamily,
          fontSize: 13,
          fontWeight: 400,
          color: APP.textPrimary,
          lineHeight: '16px',
          textAlign: 'left',
        }}
      >
        <StarIcon />
        {item.pinned ? 'Unstar' : 'Star'}
      </button>

      {/* Add to project */}
      <div
        style={{ position: 'relative' }}
        onMouseEnter={() => { setActiveIdx(2); setFolderOpen(true); }}
        onMouseLeave={() => { setActiveIdx(null); setFolderOpen(false); }}
      >
        <button
          type="button"
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            padding: '8px 10px',
            gap: 10,
            borderRadius: 6,
            backgroundColor: activeIdx === 2 ? APP.bgMenuHover : 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontFamily: APP.fontFamily,
            fontSize: 13,
            fontWeight: 400,
            color: APP.textPrimary,
            lineHeight: '16px',
            textAlign: 'left',
          }}
        >
          <FolderMenuIcon />
          Add to project
          <span style={{ marginLeft: 'auto', flexShrink: 0 }}><FolderChevron /></span>
        </button>
        {folderOpen && folders.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '100%',
              marginLeft: 4,
              width: 180,
              borderRadius: 9,
              padding: 5,
              backgroundColor: APP.bgContextMenu,
              border: `1px solid ${APP.borderContextMenu}`,
              boxShadow: APP.shadowContextMenu,
              zIndex: 1001,
            }}
          >
            {folders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                onClick={() => { onAddToFolder(folder.id); onClose(); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  padding: '8px 10px',
                  gap: 10,
                  borderRadius: 6,
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: APP.fontFamily,
                  fontSize: 13,
                  fontWeight: 400,
                  color: APP.textPrimary,
                  lineHeight: '16px',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = APP.bgMenuHover; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
              >
                {folder.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{ height: 1, margin: '3px 4px', backgroundColor: APP.border, flexShrink: 0 }} />

      {/* Delete */}
      <button
        type="button"
        onClick={() => { onDelete(); onClose(); }}
        onMouseEnter={() => setActiveIdx(3)}
        onMouseLeave={() => setActiveIdx(null)}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '8px 10px',
          gap: 10,
          borderRadius: 6,
          backgroundColor: activeIdx === 3 ? APP.bgMenuHover : 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: APP.fontFamily,
          fontSize: 13,
          fontWeight: 400,
          color: '#C0392B',
          lineHeight: '16px',
          textAlign: 'left',
        }}
      >
        <TrashMenuIcon />
        Delete
      </button>
    </div>
    </>
  );
}

// ─── Recent row ─────────────────────────────────

/** Strip file extensions and replace separators with spaces for cleaner display. */
function cleanTitle(name: string): string {
  return name
    .replace(/\.(pdf|docx?|xlsx?|pptx?|png|jpe?g|txt|csv|rtf|odt)$/i, '')
    .replace(/[_-]/g, ' ')
    .trim();
}

function RecentRow({
  item,
  folders,
  isActive,
  isLast,
  onOpen,
  onRename,
  onStar,
  onAddToFolder,
  onDelete,
}: {
  item: RecentItem;
  folders: Folder[];
  isActive: boolean;
  isLast?: boolean;
  onOpen: () => void;
  onRename: () => void;
  onStar: () => void;
  onAddToFolder: (folderId: string) => void;
  onDelete: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);
  const fileType = item.fileType || getFileTypeFromTitle(item.title);
  const subtitle = [item.operation, timeAgo(item.lastMessageAt)].filter(Boolean).join(' · ');

  const handleClose = useCallback(() => {
    setMenuOpen(false);
    setMenuAnchorRect(null);
    setHovered(false);
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        paddingBlock: 14,
        paddingInline: 16,
        gap: 12,
        borderBottom: isLast ? undefined : `1px solid ${APP.borderRecentRow}`,
        backgroundColor: hovered || isActive ? APP.bgRowHover : undefined,
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { if (!menuOpen) setHovered(false); }}
      onClick={onOpen}
    >
      <FileTypeBadge type={fileType} />

      <div style={{ flexGrow: 1, flexShrink: 1, flexBasis: '0%', overflow: 'hidden' }}>
        <div
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical' as const,
            WebkitLineClamp: 2,
            overflow: 'hidden',
            fontSize: 13,
            fontWeight: 500,
            color: APP.textPrimary,
            lineHeight: '18px',
            fontFamily: APP.fontFamily,
          }}
        >
          {cleanTitle(item.title)}
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 11,
            fontWeight: 400,
            color: APP.textMuted,
            lineHeight: '14px',
            fontFamily: APP.fontFamily,
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* Risk chip */}
      {item.riskNudge && (
        <span
          title={
            item.riskNudge === 'reconsider'
              ? `${item.highRiskCount ?? 0} high-risk — may deserve reconsideration`
              : `${item.highRiskCount ?? 0} high, ${item.mediumRiskCount ?? 0} med`
          }
          style={{
            display: 'flex', alignItems: 'center', gap: 3,
            padding: '2px 6px', borderRadius: 3, flexShrink: 0,
            fontSize: 9, fontWeight: 600, fontFamily: APP.fontFamily, lineHeight: '12px',
            whiteSpace: 'nowrap',
            backgroundColor: item.riskNudge === 'reconsider' ? '#FFF3EE' : '#FFF8F0',
            color: item.riskNudge === 'reconsider' ? '#C04A2A' : '#D97645',
            border: `1px solid ${item.riskNudge === 'reconsider' ? '#E8C4B0' : '#F0DBC8'}`,
          }}
        >
          <svg width={8} height={8} viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          {item.highRiskCount != null && item.highRiskCount > 0
            ? `${item.highRiskCount} high`
            : 'Risk'}
        </span>
      )}

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          const shouldOpen = !menuOpen;
          setMenuOpen(shouldOpen);
          setMenuAnchorRect(shouldOpen ? e.currentTarget.getBoundingClientRect() : null);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: 6,
          backgroundColor: hovered || menuOpen ? APP.bgDotsHover : 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          transition: 'background-color 150ms ease',
        }}
      >
        <DotsIcon />
      </button>

      {menuOpen && menuAnchorRect && (
        <ContextMenu
          item={item}
          folders={folders}
          anchorRect={menuAnchorRect}
          onRename={onRename}
          onStar={onStar}
          onAddToFolder={onAddToFolder}
          onDelete={onDelete}
          onClose={handleClose}
        />
      )}
    </div>
  );
}

// ─── Main Panel ─────────────────────────────────

export default function RecentPanel({
  items,
  folders,
  title = 'Recent',
  onOpen,
  onRename,
  onStar,
  onAddToFolder,
  onDelete,
}: RecentPanelProps) {
  const groups = groupByDate(items);
  const countLabel = items.length === 1 ? '1 conversation' : `${items.length} conversations`;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        height: '100%',
        fontFamily: APP.fontFamily,
        overflow: 'auto',
        paddingBlock: 40,
        paddingInline: 60,
        backgroundColor: APP.bgRecentPanel,
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div
          style={{
            fontSize: 28,
            fontWeight: 600,
            color: APP.textPrimary,
            lineHeight: '34px',
            letterSpacing: '-0.02em',
            marginBottom: 4,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 400,
            color: APP.textMuted,
            lineHeight: '16px',
          }}
        >
          {countLabel}
        </div>
      </div>

      {/* Date-grouped list */}
      {groups.map((group) => (
        <div key={group.label} style={{ marginBottom: 20 }}>
          {/* Section header */}
          <div
            style={{
              marginBottom: 4,
              fontSize: 12,
              fontWeight: 500,
              color: APP.textMuted,
              lineHeight: '16px',
              letterSpacing: '0.04em',
              textTransform: 'uppercase' as const,
            }}
          >
            {group.label}
          </div>

          {/* Card with rows */}
          <div
            style={{
              borderRadius: 12,
              overflow: 'hidden',
              backgroundColor: APP.bgRecentCard,
              border: `1px solid ${APP.borderRecentCard}`,
              boxShadow: APP.shadowRecentCard,
            }}
          >
            {group.items.map((item, idx) => (
              <RecentRow
                key={item.id}
                item={item}
                folders={folders}
                isActive={false}
                isLast={idx === group.items.length - 1}
                onOpen={() => onOpen(item.id)}
                onRename={() => onRename(item.id)}
                onStar={() => onStar(item.id, !item.pinned)}
                onAddToFolder={(folderId) => onAddToFolder(item.id, folderId)}
                onDelete={() => onDelete(item.id)}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Empty state */}
      {items.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            paddingBlock: 40,
            fontSize: 13,
            color: APP.textViewAll,
            lineHeight: '16px',
          }}
        >
          No {title.toLowerCase()} conversations
        </div>
      )}
    </div>
  );
}
